import { FC, Fragment, useState, useEffect, useRef, InputHTMLAttributes } from "react";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

type option = { id: string | null | number; name: string; email: string };

export type InputWithSelectHintprops = InputHTMLAttributes<HTMLInputElement> & {
  tittle: string;
  span?: string;
  error?: boolean;
  name: string;
  textDescribe?: string;
  textError?: string;
  className?: string;
  options: option[];
  selected: option | undefined;
  setSelected: any;
  onQueryChange: (query: string) => void; // Nueva prop para manejar el cambio de consulta
};

export const InputWithSelectHint: FC<InputWithSelectHintprops> = ({
  tittle,
  span,
  error,
  name,
  textDescribe,
  textError,
  className,
  options,
  selected,
  setSelected,
  onQueryChange,
  ...props
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    onQueryChange(newQuery);
  };

  const handleOptionClick = (option: option) => {
    setOpen(false);
    setSelected(option);
    setQuery(option.email); // Actualiza el input con el name del usuario seleccionado
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Fragment>
      <div className="relative" ref={containerRef}>
        <div className="flex justify-between">
          <label className="block text-sm font-medium leading-6 text-gray-600">
            {tittle}
          </label>
          <span className={`text-sm leading-6 text-gray-500`}>{span}</span>
        </div>

        <div className="relative">
          <input
            {...props}
            value={query}
            className={`px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
              error === true
                ? "ring-red-300 placeholder:text-red-300"
                : "ring-gray-300 placeholder:text-gray-400"
            } focus:ring-2 focus:ring-inset ${
              error === true ? "focus:ring-red-500" : "focus:ring-primary"
            } disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 disabled:ring-gray-200 sm:text-sm sm:leading-6`}
            onClick={() => setOpen(true)}
            onChange={handleQueryChange}
          />
          {(open && options.length > 0) && (
            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {options.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleOptionClick(option)}
                  className={`relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                    selected?.id === option.id ? "bg-primary-500 text-white" : "text-gray-900"
                  }`}
                >
                  <span
                    className={`block truncate ${
                      selected?.id === option.id ? "font-medium" : "font-normal"
                    }`}
                  >
                    {option.name} ({option.email})
                  </span>
                </div>
              ))}
            </div>
          )}

          {error === true && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ExclamationCircleIcon
                aria-hidden="true"
                className="h-5 w-5 text-red-500"
              />
            </div>
          )}
        </div>

        {error === true && (
          <p className={`mt-1 text-sm text-red-600`}>{textError}</p>
        )}

        {/* Descripción en gris */}
        <p className="mt-1 text-sm text-gray-400">{textDescribe}</p>
        {error && <p className={`mt-1 text-sm text-red-600`}>{error}</p>}
      </div>
    </Fragment>
  );
};